import React from "react";
import styled from "styled-components";
import { getCommaNumber } from "utils/format";
import BarChart from "components/ui/chart/Bar";
import iconChevron from "assets/icons/icon_chevron_black.svg";
import type { SeriesItem } from "types/ManagerPayment";

// 축 라벨이 길면(주간 "7/6~7/12", 월간 "2026.07") 서로 겹치므로 2줄로 나눈다.
// 일간 "7/28"처럼 구분자가 없으면 한 줄 그대로 둔다.
const toAxisLabel = (
  label: string,
  currentYear: number
): string | string[] => {
  const tilde = label.indexOf("~");
  if (tilde >= 0) {
    const start = label.slice(0, tilde);
    const end = label.slice(tilde + 1);
    // 진행 중인 주의 첫날은 시작==끝("8/10~8/10")이라 날짜 하나만 표시
    if (start === end) return start;
    // 주간: "7/6~7/12" → ["7/6~", "7/12"]
    return [start + "~", end];
  }
  const dot = label.indexOf(".");
  if (dot >= 0) {
    // 월간: 올해면 연도 숨기고 월만("8월"), 다른 해면 연도+월 2줄(["2025","8월"])
    const year = label.slice(0, dot);
    const month = parseInt(label.slice(dot + 1), 10) + "월";
    return String(currentYear) === year ? month : [year, month];
  }
  return label;
};

export const seriesToChartData = (series: SeriesItem[]) => {
  const currentYear = new Date().getFullYear();
  const data = series.map((s) => s.amount);
  return {
    labels: series.map((s) => toAxisLabel(s.label, currentYear)),
    datasets: [
      {
        data,
        backgroundColor: data.map((v) => (v < 0 ? "#f43e3e" : "#3b79f0")),
        borderRadius: { topLeft: 4, topRight: 4 },
        barPercentage: 0.4,
        categoryPercentage: 1,
      },
    ],
  };
};

interface Props {
  label: string;
  totalAmount: number;
  series?: SeriesItem[];
  isChart?: boolean;
  onTotalClick?: () => void;
  children: React.ReactNode;
}

export const PaymentSummaryCard = ({
  label,
  totalAmount,
  series,
  isChart = true,
  onTotalClick,
  children,
}: Props) => {
  return (
    <Wrapper>
      <div className="top">
        <div className="summaryInfo">
          <h4>{label}</h4>
          {onTotalClick ? (
            <a onClick={onTotalClick}>
              <h2>{getCommaNumber(totalAmount)}원</h2>
            </a>
          ) : (
            <h2>{getCommaNumber(totalAmount)}원</h2>
          )}
        </div>
        {isChart && series && series.length ? (
          <div className="chartInfo">
            <BarChart data={seriesToChartData(series)} height={150} />
          </div>
        ) : null}
      </div>
      <div className="middle">{children}</div>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  .top {
    padding-bottom: 20px;
  }
  .top .summaryInfo {
    text-align: center;
  }
  .top .summaryInfo h4 {
    font-size: 20px;
    font-weight: 400;
  }
  .top .summaryInfo h2 {
    margin-top: 2px;
    font-size: 28px;
    font-weight: 700;
  }
  .top a {
    display: inline-flex;
    margin-top: 4px;
    padding-right: 1.25em;
    background: url(${iconChevron}) no-repeat center right / 12px;
  }
  .top .chartInfo {
    min-height: 150px;
  }
`;

export default PaymentSummaryCard;
